import { t } from '@hefe/i18n';
import { AnalysisIssuesOverviewSection } from '@hefe/models';
import { currentUserSelector, IssuesApi } from '@hefe/store';
import {
  Alert,
  Button,
  Flex,
  Image,
  Segmented,
  Select,
  Typography,
} from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import smartThommyWink from '../../../../../assets/images/smart_thommy/st_wink.png';
import IssuesListComponent from '../issues-list/issues-list.component';

export default function IssuesDrawerComponent() {
  // Shares the same RTK Query cache entry as the navigation badge, no extra request triggered
  const { data: overview } = IssuesApi.useFetchIssuesOverviewQuery();

  const user = useSelector(currentUserSelector);

  const [showDetails, setShowDetails] = useState(false);
  const [selectedSectionKey, setSelectedSectionKey] = useState<string>();

  const sections = overview?.sections ?? [];

  useEffect(() => {
    if (sections.length === 0) {
      return;
    }

    // Bestehende Auswahl respektieren.
    if (
      selectedSectionKey !== null &&
      sections.some((section) => section.key === selectedSectionKey)
    ) {
      return;
    }

    setSelectedSectionKey(getPreferredSection(sections).key);
  }, [sections, selectedSectionKey]);

  const selectedSection = sections.find(
    (section) => section.key === selectedSectionKey
  );

  const getPreferredSection = (sections: AnalysisIssuesOverviewSection[]) => {
    const personal = sections.find((section) => section.type === 'personal');

    const club = sections.find((section) => section.type === 'club');

    if (personal && personal.count > 0) {
      return personal;
    }

    if (club && club.count > 0) {
      return club;
    }

    const teamWithMostIssues = sections
      .filter((section) => section.type === 'team')
      .sort((a, b) => b.count - a.count)[0];

    if (teamWithMostIssues && teamWithMostIssues.count > 0) {
      return teamWithMostIssues;
    }

    return personal ?? club ?? teamWithMostIssues ?? sections[0];
  };

  const sectionText = (section: AnalysisIssuesOverviewSection) => {
    switch (section.type) {
      case 'personal':
        return t('issues.court_vision.sections.personal');
      case 'club':
        return t('issues.court_vision.sections.club');
      default:
        return section.label;
    }
  };

  const sectionList = useMemo(() => {
    if (sections.length === 0) {
      return [];
    }

    const data = sections.map((section) => ({
      label: `${sectionText(section)} (${section.count})`,
      value: section.key,
    }));

    if (sections.length > 5) {
      return (
        <Select
          style={{ width: '100%' }}
          value={selectedSectionKey}
          onChange={(value) => setSelectedSectionKey(value as string)}
          options={data}
        />
      );
    }

    return (
      <Segmented
        value={selectedSectionKey}
        onChange={(value) => setSelectedSectionKey(value as string)}
        options={sections.map((section) => ({
          label: `${sectionText(section)} (${section.count})`,
          value: section.key,
        }))}
      />
    );
  }, [sections, selectedSectionKey]);

  return (
    <Flex vertical gap={16}>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Image
          style={{ width: 200, height: 200, objectFit: 'contain' }}
          src={smartThommyWink}
          alt="Winky Smart Thommy"
          preview={false}
        />
        <Typography.Paragraph>
          {t('smart_assistant.intro.text', { name: user?.firstname || '' })}
        </Typography.Paragraph>
      </div>
      <Alert
        type="info"
        showIcon
        message={t('issues.court_vision.title')}
        description={
          <Flex vertical gap={8}>
            {showDetails && (
              <>
                <Typography.Text strong>
                  {t('issues.court_vision.beta.description')}
                </Typography.Text>
                <Typography.Text>
                  {t('issues.court_vision.beta.lines.1')}
                  {t('issues.court_vision.beta.lines.2')}
                  {t('issues.court_vision.beta.lines.3')}
                  {t('issues.court_vision.beta.lines.4')}
                </Typography.Text>
              </>
            )}

            <Button type="link" onClick={() => setShowDetails((old) => !old)}>
              {t(
                'issues.court_vision.beta.show_more.' +
                  (showDetails ? 'less' : 'more')
              )}
            </Button>
          </Flex>
        }
      />

      {sections.length > 0 && (
        <div style={{ overflowX: 'auto', maxWidth: '100%' }}>{sectionList}</div>
      )}

      {selectedSection && (
        <IssuesListComponent
          key={selectedSection.key}
          section={selectedSection}
        />
      )}
    </Flex>
  );
}
